import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';

import UrlTextField from '~/components/atoms/UrlTextField';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { VCardPlusWebsite } from '~/types/qrCode';
import { isMaxLength } from '~/utils/common';

const iconButtonSx = { width: '40px', height: '40px' };

type VCarPlusContactInfoWebsiteItemProps = {
  website: VCardPlusWebsite;
  deleteVCardPlusWebsite: (websiteId: number) => void;
  updateVCardPlusWebsite: (params: { body: any; websiteId: number }) => void;
  isPendingDeleteVCardPlusWebsite: boolean;
};

export default function VCarPlusContactInfoWebsiteItem({
  website,
  updateVCardPlusWebsite,
  deleteVCardPlusWebsite,
  isPendingDeleteVCardPlusWebsite,
}: VCarPlusContactInfoWebsiteItemProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [websiteTitle, setWebsiteTitle] = useState(website.title);
  const [websiteURL, setWebsiteURL] = useState(website.url);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-end"
      sx={{ flexDirection: isDesktop ? 'row' : 'column', gap: '16px', marginBottom: '16px' }}
    >
      <StyledSectionHeader>
        <TextInput
          value={websiteTitle}
          onChange={(e) => setWebsiteTitle(e.target.value)}
          onBlur={() => updateVCardPlusWebsite({ body: { title: websiteTitle }, websiteId: website.id })}
          inputProps={{ maxLength: 100 }}
          helperText={isMaxLength(websiteTitle, 100)}
          fullWidth
          label={t('common:personalWebsite')}
          placeholder={`${t('other:label')}...`}
          absoluteHelperText={isDesktop}
        />
        {!isDesktop && (
          <IconButton
            aria-label="delete"
            disabled={isPendingDeleteVCardPlusWebsite}
            onClick={() => deleteVCardPlusWebsite(website.id)}
            sx={iconButtonSx}
          >
            <ClearIcon />
          </IconButton>
        )}
      </StyledSectionHeader>
      <UrlTextField
        requireProtocol={false}
        urlValue={websiteURL}
        placeholder="https://www.example.com"
        onChange={(e) => {
          setWebsiteURL(e.target.value);
        }}
        onBlur={({ isValid }) =>
          isValid && updateVCardPlusWebsite({ body: { url: websiteURL }, websiteId: website.id })
        }
        absoluteHelperText={isDesktop}
        sx={{ flex: 1 }}
      />
      {isDesktop && (
        <IconButton
          aria-label="delete"
          disabled={isPendingDeleteVCardPlusWebsite}
          onClick={() => deleteVCardPlusWebsite(website.id)}
          sx={iconButtonSx}
        >
          <ClearIcon />
        </IconButton>
      )}
    </Box>
  );
}

export const StyledSectionHeader = styled('div')(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
    gap: '16px',
  },
}));
